/**
 * Колонки канбана из доски и плоского списка задач — одни для веба
 * и приложения.
 *
 * Задачи раскладываются по столбцам на клиенте, а не на сервере: сервер
 * отдаёт плоский список, и это правильно — фильтры не должны менять форму
 * ответа.
 */

import { ProjectBindingMode } from "@taskflow/contracts";

import type { TaskListItem } from "../tasks/api";
import type { BoardDetail } from "./api";
import type { KanbanColumnData } from "./kanbanTypes";
import { sortByStatus } from "./sortByStatus";
import { DEFAULT_VIEW_MARK_COLOR } from "./viewMark";

export function buildKanbanColumns(
  board: BoardDetail,
  tasks: readonly TaskListItem[],
): KanbanColumnData[] {
  const byColumn = new Map<string, TaskListItem[]>();
  for (const column of board.columns) byColumn.set(column.id, []);
  for (const task of tasks) byColumn.get(task.columnId)?.push(task);

  const columnMode = board.projectBindingMode === ProjectBindingMode.COLUMN;
  // Сортировка по статусу — только в режиме «проект для каждого столбца»:
  // в режиме доски статус и есть столбец.
  const statusOrder = board.sortByStatus && columnMode;
  // Просмотренные задачи закрашиваются, только пока режим включён.
  // Отметки и цвет личные: сервер отдаёт viewed только по отметкам
  // смотрящего за сегодня, а viewMarkColor — его собственный цвет.
  const markColor =
    board.viewMarkEnabled && columnMode ? (board.viewMarkColor ?? DEFAULT_VIEW_MARK_COLOR) : null;

  return board.columns
    .filter((column) => column.isActive)
    .map((column) => {
      const cards = (byColumn.get(column.id) ?? []).map((card) =>
        markColor !== null && card.viewed === true ? { ...card, highlightColor: markColor } : card,
      );
      return {
        id: column.id,
        name: column.name,
        projectName: column.projectName,
        allowedTargets: column.allowedTargets,
        requiresAssignee: column.requireAssigneeOnEntry,
        cards: statusOrder ? sortByStatus(cards) : cards,
      };
    });
}
